import { useQuery } from 'react-query';
import axios from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import type {
  PublishHistoryEntry,
  PublishHistoryResponse
} from '@utils/types/publishing/publish.ts';

async function fetchDocumentPublishHistory(
  sessionId: string,
  documentId: string
): Promise<PublishHistoryEntry[]> {
  try {
    const { data } = await Axios.get<PublishHistoryResponse>(
      GET_URL(sessionId, documentId).documentPublishHistory
    );
    return data.result;
  } catch (err) {
    // Backend returns 404 when a document has never been published. Treat it as
    // "no history yet" instead of surfacing the error — otherwise react-query
    // retries 3× and the console.error leaks past the Sentry filters.
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return [];
    }
    throw err;
  }
}

export function useGetDocumentPublishHistory(
  sessionId: string | undefined,
  documentId: string | undefined,
  enabled: boolean = true
) {
  return useQuery<PublishHistoryEntry[]>(
    QueryKeys.documentPublishHistory(sessionId ?? '', documentId ?? ''),
    () =>
      fetchDocumentPublishHistory(sessionId as string, documentId as string),
    {
      enabled: enabled && !!sessionId && !!documentId,
      staleTime: 1000 * 30,
      useErrorBoundary: false,
      refetchOnWindowFocus: false
    }
  );
}
